import { getAuthenticatedAxios, getWebSocketUrl } from "../../../utils/api";
import { extractPmidFromUrl } from "../../../shared/utils/paper";
import { MAX_PAPER_ANALYSIS_SOURCES } from "../constants/paperAnalysis";

const DEFAULT_ERROR_MESSAGE = "Paper analysis failed. Please try again.";

export const PAPER_ANALYSIS_ERROR_MESSAGE_KEYS = Object.freeze({
  paper_analysis_not_implemented:
    "paperAnalysis.errors.codes.paper_analysis_not_implemented",
  paper_analysis_validation_failed:
    "paperAnalysis.errors.codes.paper_analysis_validation_failed",
  paper_analysis_full_text_unavailable:
    "paperAnalysis.errors.codes.paper_analysis_full_text_unavailable",
  paper_analysis_abstract_missing:
    "paperAnalysis.errors.codes.paper_analysis_abstract_missing",
  paper_analysis_context_too_large:
    "paperAnalysis.errors.codes.paper_analysis_context_too_large",
  paper_analysis_query_required:
    "paperAnalysis.errors.codes.paper_analysis_query_required",
  paper_analysis_generation_failed:
    "paperAnalysis.errors.codes.paper_analysis_generation_failed",
  paper_analysis_default_sources_unavailable:
    "paperAnalysis.errors.codes.paper_analysis_default_sources_unavailable",
  invalid_json: "paperAnalysis.errors.codes.invalid_json",
  invalid_payload: "paperAnalysis.errors.codes.invalid_payload",
  unsupported_event_type: "paperAnalysis.errors.codes.unsupported_event_type",
  connection_error: "paperAnalysis.errors.codes.connection_error",
});

const safeParseMessage = (rawData) => {
  if (!rawData) return null;
  if (typeof rawData === "object") return rawData;
  if (typeof rawData !== "string") return null;

  try {
    const parsed = JSON.parse(rawData);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (_error) {
    return null;
  }
};

const buildPaperAnalysisError = (
  message = DEFAULT_ERROR_MESSAGE,
  { code = null, details = null } = {},
) => {
  const error = new Error(message || DEFAULT_ERROR_MESSAGE);
  error.code = code || null;
  error.details = details || null;
  return error;
};

const extractErrorCode = (error) => {
  const code =
    error?.code ||
    error?.response?.data?.code ||
    error?.response?.data?.error_code ||
    null;
  return typeof code === "string" && code.trim() ? code.trim() : null;
};

export const paperAnalysisErrorMessage = (
  error,
  fallbackMessage = DEFAULT_ERROR_MESSAGE,
  translate = null,
) => {
  const code = extractErrorCode(error);
  const messageKey = code ? PAPER_ANALYSIS_ERROR_MESSAGE_KEYS[code] : null;
  if (messageKey && typeof translate === "function") {
    return translate(messageKey);
  }

  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail) return JSON.stringify(detail);
  if (typeof error?.message === "string" && error.message.trim()) return error.message;
  return fallbackMessage;
};

export const buildPaperAnalysisPaperRef = (paper = {}) => {
  const pmid = paper.pmid || paper.PMID || extractPmidFromUrl(paper.url);
  const pmcid = paper.pmcid || paper.PMCID || null;
  const url = paper.url || paper.URL || null;

  return {
    ...(pmid ? { pmid: String(pmid) } : {}),
    ...(pmcid ? { pmcid: String(pmcid) } : {}),
    ...(url ? { url: String(url) } : {}),
  };
};

export const getPaperAnalysisThread = async ({ searchRunId }) => {
  if (!searchRunId) {
    throw new Error("Missing search_run_id for paper analysis history.");
  }

  const authAxios = getAuthenticatedAxios();
  const response = await authAxios.get(
    `/api/v2/paper-analysis/search-runs/${encodeURIComponent(searchRunId)}`,
  );

  return response.data?.thread || null;
};

export const requestPaperAnalysisTurn = ({
  parentSearchRunId,
  papers = [],
  paperRefs = null,
  message,
  query,
  language,
  searchMode,
  onChunk,
  onUsageUpdated,
}) => {
  return new Promise((resolve, reject) => {
    if (!message || !String(message).trim()) {
      reject(new Error("Paper analysis message is required."));
      return;
    }

    let websocket = null;
    let settled = false;
    let resultPayload = null;

    const settle = (resolver, value) => {
      if (settled) return;
      settled = true;
      try {
        websocket?.close();
      } catch (_error) {
        // Connection may already be closed by the server.
      }
      resolver(value);
    };

    try {
      if (!parentSearchRunId) {
        throw new Error("Missing parent_search_run_id for paper analysis.");
      }

      const refs = Array.isArray(paperRefs)
        ? paperRefs
        : (Array.isArray(papers) ? papers : [])
            .map((paper) => buildPaperAnalysisPaperRef(paper))
            .filter((paperRef) => paperRef.pmid || paperRef.pmcid || paperRef.url);
      if (refs.length > MAX_PAPER_ANALYSIS_SOURCES) {
        throw new Error(
          `Paper analysis supports at most ${MAX_PAPER_ANALYSIS_SOURCES} selected sources.`,
        );
      }

      const payload = {
        message: {
          message_content: String(message).trim(),
          created_at: new Date().toISOString(),
        },
        thread: {
          parent_search_run_id: parentSearchRunId,
          paper_refs: refs,
          query: query || null,
          language: language === "en" || language === "zh" ? language : "ja",
          search_mode: searchMode === "pro" ? "pro" : "basic",
        },
      };

      websocket = new WebSocket(getWebSocketUrl("v2"));
      websocket.onopen = () => {
        websocket.send(JSON.stringify({ type: "paper_analysis_turn", payload }));
      };
      websocket.onerror = () => {
        settle(reject, buildPaperAnalysisError(DEFAULT_ERROR_MESSAGE));
      };
      websocket.onmessage = (event) => {
        const data = safeParseMessage(event.data);
        if (!data) return;

        if (data.type === "workflow_intermediate") {
          const chunk = data.payload?.chunk;
          if (typeof chunk === "string" && chunk) {
            onChunk?.(chunk);
          }
          return;
        }

        if (data.type === "workflow_result") {
          resultPayload = data.payload;
          return;
        }

        if (data.type === "usage_updated") {
          if (data.current_usage !== undefined) {
            onUsageUpdated?.(data.current_usage, data.limit);
          }
          return;
        }

        if (data.type === "workflow_status" && data.status === "completed") {
          if (resultPayload) {
            settle(resolve, resultPayload);
            return;
          }
          settle(
            reject,
            buildPaperAnalysisError(
              "Paper analysis completed without a result payload.",
              { code: "invalid_payload" },
            ),
          );
          return;
        }

        if (data.type === "workflow_error") {
          settle(
            reject,
            buildPaperAnalysisError(data.message || DEFAULT_ERROR_MESSAGE, {
              code: data.code,
              details: data.details,
            }),
          );
        }
      };
      websocket.onclose = () => {
        if (!settled) {
          settle(
            reject,
            buildPaperAnalysisError(
              "Paper analysis connection closed before completion.",
              { code: "connection_error" },
            ),
          );
        }
      };
    } catch (error) {
      settle(reject, error);
    }
  });
};
